import React from "react";
import { Container, Typography, Box, Divider } from "@mui/material";
import SeoHead from "../../Components/SeoHead";
import { webPageSchema } from "src/utils/seoSchema";

const TermsAndPrivacy = () => {
  const description =
    "Review the Terms of Service and Privacy Policy for STR Monitoring, short-term rental monitoring for cities, local governments, and HOAs.";

  return (
    <>
      <SeoHead
        title="Terms of Service and Privacy Policy | STR Monitoring"
        description={description}
        keywords="terms of service, privacy policy, short term rental monitoring, rental monitoring, HOA, local government"
        canonical="/terms-privacy"
        ogTitle="Terms of Service and Privacy Policy | Short-Term Rental Monitoring"
        ogDescription="Terms of use, data accuracy disclaimers, marketing rights, and how STR Monitoring protects your privacy."
        ogImage="/images/og-default.jpg"
        jsonLd={webPageSchema({
          name: "Terms of Service and Privacy Policy",
          description,
          url: "/terms-privacy",
        })}
      />

      <Container
        sx={{
          textAlign: "start",
          py: 4,
          color: "#cbd5e1",
          "& .MuiTypography-h4, & .MuiTypography-h6": { color: "#fff" },
          "& .MuiTypography-body1": { color: "#94a3b8" },
        }}
        maxWidth={"lg"}
      >
        {/* Terms of Service Section */}
        <Box my={4}>
          <Typography variant="h4" gutterBottom>
            Terms of Service
          </Typography>
          <Typography variant="body1" paragraph>
            Last updated: September 11, 2026
          </Typography>
          <Typography variant="body1" paragraph>
            Welcome to strmonitoring.com (the &quot;Site&quot;), operated by STR
            Monitoring (&quot;STR Monitoring,&quot; &quot;we,&quot;
            &quot;us,&quot; or &quot;our&quot;). By accessing or using the Site
            or related online materials, you agree to these Terms of Service
            (the &quot;Terms&quot;). If you do not agree, do not use the Site.
          </Typography>

          <Typography variant="h6" gutterBottom>
            1. Acceptance; Relationship to Contracts
          </Typography>
          <Typography variant="body1" paragraph>
            These Terms govern your use of the Site and publicly available
            information about our services. If you purchase or receive paid
            monitoring, software, or related services, a separate master service
            agreement, statement of work, order form, or written quote (each, a
            &quot;Service Agreement&quot;) will apply. If a Service Agreement
            conflicts with these Terms, the Service Agreement controls for that
            engagement. Website use alone does not create a paid customer
            relationship. International visitors and customers outside the
            United States may request invoices in local currency (for example
            EUR, MXN, CAD, or GBP); Service Agreements may add
            jurisdiction-specific schedules while these Terms continue to govern
            Site use unless a signed agreement says otherwise.
          </Typography>

          <Typography variant="h6" gutterBottom>
            2. Our Services
          </Typography>
          <Typography variant="body1" paragraph>
            STR Monitoring provides short-term rental (STR) listing discovery,
            monitoring, alerts, maps, and related tools intended for cities,
            local governments, HOAs, multifamily operators, and similar
            organizations. Descriptions on the Site are for general information
            only. Our tools are not legal advice, are not a substitute for code
            enforcement, permitting, legal counsel, or your internal procedures,
            and do not create an attorney-client or government-agency
            relationship.
          </Typography>

          <Typography variant="h6" gutterBottom>
            3. Eligibility and Accurate Information
          </Typography>
          <Typography variant="body1" paragraph>
            You represent that you have authority to use the Site on behalf of
            yourself or your organization. You agree to provide accurate
            information when submitting contact forms or inquiries and to keep
            that information reasonably current. You may not use the Site if
            doing so would violate applicable law.
          </Typography>

          <Typography variant="h6" gutterBottom>
            4. Acceptable Use
          </Typography>
          <Typography variant="body1" paragraph>
            You agree to use the Site only for lawful purposes. You may not: (a)
            attempt to gain unauthorized access to systems, accounts, or data;
            (b) scrape, crawl, or harvest the Site in a manner that burdens or
            disrupts our infrastructure, except as allowed by public search
            engines following robots.txt; (c) reverse engineer, decompile, or
            attempt to extract source code or proprietary methods from our
            software or Site, except where applicable law prohibits this
            restriction; (d) interfere with or disrupt the Site; or (e) use
            information obtained from us to harass, stalk, or unlawfully target
            individuals outside of legitimate ordinance, bylaw, licensing, or
            community-rule enforcement workflows you are authorized to perform.
            Unauthorized use may result in civil claims and, where applicable,
            criminal liability.
          </Typography>

          <Typography variant="h6" gutterBottom>
            5. Data Accuracy; No Reliance
          </Typography>
          <Typography variant="body1" paragraph>
            Listing, map, host, location, pricing, availability, and related
            data we display or deliver—including data sourced from Airbnb,
            VRBO, and similar platforms—may be incomplete, delayed, inaccurate,
            or approximate. Platforms often obfuscate exact addresses. You are
            solely responsible for independently verifying any information
            before taking enforcement action, issuing citations, pursuing
            licensing or tax matters, contacting property owners or hosts, or
            relying on the data for any legal, operational, or financial
            decision. We do not warrant that every listing in a geography will
            be discovered or that results will be continuous or error-free.
          </Typography>

          <Typography variant="h6" gutterBottom>
            6. No Legal or Enforcement Guarantees
          </Typography>
          <Typography variant="body1" paragraph>
            We do not guarantee ordinance or bylaw compliance outcomes, citation
            or prosecution success, tax or fee recovery, reduction in complaints,
            or any particular enforcement result. Outcomes depend on your laws,
            policies, staffing, evidence standards, and third-party platforms
            outside our control.
          </Typography>

          <Typography variant="h6" gutterBottom>
            7. Quotes and Pricing
          </Typography>
          <Typography variant="body1" paragraph>
            Pricing calculators, estimates, and marketing figures on the Site
            are non-binding planning tools only. They are not offers. Only a
            written quote or Service Agreement signed or expressly accepted by
            STR Monitoring creates pricing or service obligations.
          </Typography>

          <Typography variant="h6" gutterBottom>
            8. Intellectual Property
          </Typography>
          <Typography variant="body1" paragraph>
            The Site and all content, software, dashboards, reports, methods,
            logos, graphics, and branding are owned by STR Monitoring or its
            licensors and are protected by copyright, trademark, and other
            intellectual property laws. We grant you a limited, revocable,
            non-exclusive, non-transferable license to access and use the Site
            for legitimate evaluation and informational purposes. You may not
            copy, modify, distribute, sell, or create derivative works from our
            materials without prior written consent, except for fair use or
            other rights that cannot be waived under applicable law.
          </Typography>

          <Typography variant="h6" gutterBottom>
            9. Feedback
          </Typography>
          <Typography variant="body1" paragraph>
            If you provide ideas, suggestions, or feedback, you grant STR
            Monitoring a perpetual, worldwide, royalty-free license to use and
            incorporate that feedback without obligation or compensation to you.
          </Typography>

          <Typography variant="h6" gutterBottom>
            10. Marketing and Case Studies
          </Typography>
          <Typography variant="body1" paragraph>
            You grant STR Monitoring a non-exclusive, worldwide, royalty-free
            right to use anonymized results, metrics, insights, and
            descriptions derived from your inquiry, evaluation, or use of our
            services for case studies, marketing, sales materials, websites,
            blogs, proposals, presentations, advertisements, and discussions
            with other prospective customers—provided we do not identify your
            city, jurisdiction, HOA, property, or organization by name without
            your prior written consent. We may describe outcomes in aggregate
            (for example, listing counts, detection trends, or compliance
            themes) and may refer to the type of engagement (for example,
            &quot;a mid-sized city&quot; or &quot;an HOA client&quot;) when
            speaking with other prospects. Named-client references, logos, and
            testimonials require separate written permission and are typically
            addressed in a Service Agreement. You may opt out of future
            anonymized marketing use by written notice to
            customerservice@strmonitoring.com; opt-out applies prospectively and
            does not require removal of materials already created or published.
          </Typography>

          <Typography variant="h6" gutterBottom>
            11. Third-Party Services and Links
          </Typography>
          <Typography variant="body1" paragraph>
            The Site and our services may rely on or link to third-party
            providers, including map and geocoding services (such as Mapbox,
            Google Maps, and OpenStreetMap/Nominatim), U.S. Census data,
            analytics providers, email delivery, hosting, and short-term rental
            platforms. Their terms and privacy practices apply to their
            services. We are not responsible for third-party content,
            availability, or practices.
          </Typography>

          <Typography variant="h6" gutterBottom>
            12. Disclaimer of Warranties
          </Typography>
          <Typography variant="body1" paragraph>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE SITE AND ALL
            INFORMATION, TOOLS, AND MATERIALS ARE PROVIDED &quot;AS IS&quot; AND
            &quot;AS AVAILABLE,&quot; WITHOUT WARRANTIES OF ANY KIND, WHETHER
            EXPRESS, IMPLIED, OR STATUTORY, INCLUDING WARRANTIES OF
            MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE,
            NON-INFRINGEMENT, ACCURACY, OR UNINTERRUPTED OR ERROR-FREE ACCESS.
            Some jurisdictions do not allow certain disclaimers; in those cases,
            disclaimers apply to the fullest extent permitted.
          </Typography>

          <Typography variant="h6" gutterBottom>
            13. Limitation of Liability
          </Typography>
          <Typography variant="body1" paragraph>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, STR MONITORING AND ITS
            OFFICERS, DIRECTORS, EMPLOYEES, AND AGENTS WILL NOT BE LIABLE FOR
            ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR
            PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, REVENUE, DATA, GOODWILL,
            OR BUSINESS OPPORTUNITY, ARISING OUT OF OR RELATED TO YOUR USE OF
            (OR INABILITY TO USE) THE SITE OR ANY INFORMATION OBTAINED FROM US,
            WHETHER BASED IN CONTRACT, TORT, STRICT LIABILITY, OR OTHERWISE,
            EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGES. OUR TOTAL
            LIABILITY FOR CLAIMS ARISING OUT OF THE SITE OR THESE TERMS WILL
            NOT EXCEED THE GREATER OF (A) THE AMOUNTS YOU PAID US FOR THE
            APPLICABLE PAID SERVICE DURING THE TWELVE (12) MONTHS BEFORE THE
            CLAIM, OR (B) ONE HUNDRED U.S. DOLLARS (US $100) IF YOU HAVE NOT
            PAID US FOR A SERVICE RELATED TO THE CLAIM. Liability limits in a
            Service Agreement, if any, control for that engagement.
          </Typography>

          <Typography variant="h6" gutterBottom>
            14. Indemnification
          </Typography>
          <Typography variant="body1" paragraph>
            You agree to defend, indemnify, and hold harmless STR Monitoring and
            its officers, directors, employees, and agents from and against
            claims, damages, losses, costs, and expenses (including reasonable
            attorneys&apos; fees) arising out of or related to: (a) your misuse
            of the Site or our data; (b) enforcement, legal, or operational
            actions you take based on information obtained from us; (c) your
            violation of these Terms or applicable law; or (d) content or
            information you submit to us.
          </Typography>

          <Typography variant="h6" gutterBottom>
            15. Termination
          </Typography>
          <Typography variant="body1" paragraph>
            We may suspend or terminate access to the Site at any time for
            breach of these Terms, suspected abuse, legal risk, or operational
            reasons. Provisions that by their nature should survive (including
            intellectual property, marketing rights already exercised,
            disclaimers, limitation of liability, indemnity, and governing law)
            will survive termination.
          </Typography>

          <Typography variant="h6" gutterBottom>
            16. Changes to These Terms
          </Typography>
          <Typography variant="body1" paragraph>
            We may update these Terms from time to time. The &quot;Last
            updated&quot; date at the top of this section will reflect changes.
            Continued use of the Site after posting constitutes acceptance of
            the revised Terms.
          </Typography>

          <Typography variant="h6" gutterBottom>
            17. Governing Law and Contact
          </Typography>
          <Typography variant="body1" paragraph>
            These Terms are governed by the laws of the State of Texas,
            excluding conflict-of-law rules. Exclusive venue for disputes
            arising out of the Site or these Terms lies in the state or federal
            courts located in Texas, and you consent to personal jurisdiction
            there, except where prohibited by law for government entities.
            Questions about these Terms: customerservice@strmonitoring.com.
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Privacy Policy Section */}
        <Box my={4}>
          <Typography variant="h4" gutterBottom>
            Privacy Policy
          </Typography>
          <Typography variant="body1" paragraph>
            Effective Date: September 11, 2026
          </Typography>
          <Typography variant="body1" paragraph>
            Your privacy is important to us. This Privacy Policy explains how
            STR Monitoring (&quot;we,&quot; &quot;us,&quot; or &quot;our&quot;)
            collects, uses, discloses, and protects personal information when
            you use strmonitoring.com and related online inquiry channels. Paid
            monitoring engagements may include additional privacy or data-processing
            terms in a Service Agreement, which control for that engagement if
            they conflict with this Policy.
          </Typography>

          <Typography variant="h6" gutterBottom>
            1. Information We Collect
          </Typography>
          <Typography variant="body1" paragraph>
            We may collect: (a) information you provide, such as name, email
            address, phone number, organization, and message content when you
            contact us or request a quote; (b) technical and usage information,
            such as IP address, browser type, device information, pages viewed,
            referring URLs, and approximate location derived from IP; (c)
            analytics data via Google Analytics 4 and similar tools; (d)
            map and search interactions when you use map, geocoding, or pricing
            tools on the Site; and (e) account, configuration, alert, and
            monitoring-related information if you later receive access to our
            product dashboards or services.
          </Typography>

          <Typography variant="h6" gutterBottom>
            2. Public and Third-Party Listing Data
          </Typography>
          <Typography variant="body1" paragraph>
            To deliver STR monitoring, we collect and process information about
            short-term rental listings that is publicly available or obtained
            from third-party platforms (for example, listing URLs, host display
            names, descriptions, prices, photos, availability indicators, and
            approximate or stated locations). This processing is for providing
            monitoring and compliance tools to our customers—not for selling a
            consumer data-broker product. Listing-related information may
            include data that identifies or relates to hosts or property
            managers as it appears on those platforms.
          </Typography>

          <Typography variant="h6" gutterBottom>
            3. How We Use Your Information
          </Typography>
          <Typography variant="body1" paragraph>
            We use information to: provide and improve our Site and services;
            respond to inquiries and prepare quotes; operate maps, analytics,
            and monitoring features; maintain security and prevent abuse; comply
            with law; and create anonymized or aggregated analytics and
            marketing materials consistent with our Terms of Service (including
            case studies that do not name your city, jurisdiction, HOA, or
            organization without consent). We do not sell your personal
            information to third parties.
          </Typography>

          <Typography variant="h6" gutterBottom>
            4. How We Share Information
          </Typography>
          <Typography variant="body1" paragraph>
            We may share information with service providers who help us operate
            our business, including email delivery (such as Resend), analytics
            (such as Google Analytics), map and geocoding providers (such as
            Mapbox, Google Maps, and OpenStreetMap/Nominatim), hosting and
            infrastructure providers, and professional advisors. We may also
            disclose information if required by law, to protect rights and
            safety, or in connection with a corporate transaction (merger, sale,
            or financing). We do not sell personal information.
          </Typography>

          <Typography variant="h6" gutterBottom>
            5. Cookies and Tracking
          </Typography>
          <Typography variant="body1" paragraph>
            We and our partners may use cookies, pixels, and similar
            technologies—including Google Analytics 4—to understand Site usage,
            improve performance, and measure marketing effectiveness. You can
            control cookies through your browser settings; blocking cookies may
            affect some Site features. For Google Analytics controls, see
            Google&apos;s published opt-out and privacy resources.
          </Typography>

          <Typography variant="h6" gutterBottom>
            6. Retention
          </Typography>
          <Typography variant="body1" paragraph>
            We retain personal information as long as needed to respond to
            inquiries, provide services, meet legal and accounting requirements,
            resolve disputes, and support legitimate business purposes,
            including anonymized or aggregated records used for analytics and
            marketing. Retention periods vary by data type and context.
          </Typography>

          <Typography variant="h6" gutterBottom>
            7. Data Security
          </Typography>
          <Typography variant="body1" paragraph>
            We implement reasonable administrative, technical, and physical
            measures designed to protect personal information. No method of
            transmission or storage is completely secure, and we cannot
            guarantee absolute security.
          </Typography>

          <Typography variant="h6" gutterBottom>
            8. Children&apos;s Privacy
          </Typography>
          <Typography variant="body1" paragraph>
            The Site is not directed to children under 13 (or under 16 where
            applicable). We do not knowingly collect personal information from
            children. If you believe we have collected such information,
            contact us and we will take appropriate steps to delete it.
          </Typography>

          <Typography variant="h6" gutterBottom>
            9. Your Privacy Rights
          </Typography>
          <Typography variant="body1" paragraph>
            Depending on your location, you may have rights to access, correct,
            delete, or obtain a copy of certain personal information, or to
            object to or restrict certain processing. To exercise these rights,
            or to opt out of future anonymized marketing use as described in our
            Terms, contact us at customerservice@strmonitoring.com. We may need
            to verify your request. Some rights may not apply to publicly
            available listing data we process for monitoring services or to
            information we must retain for legal reasons.
          </Typography>

          <Typography variant="h6" gutterBottom>
            10. International Processing
          </Typography>
          <Typography variant="body1" paragraph>
            We are based in the United States and process information in the
            United States and potentially other countries where our service
            providers operate. If you access the Site from outside the United
            States, you understand that your information may be transferred to
            and processed in the U.S., where privacy laws may differ from those
            in your jurisdiction.
          </Typography>

          <Typography variant="h6" gutterBottom>
            11. Changes to This Policy
          </Typography>
          <Typography variant="body1" paragraph>
            We may update this Privacy Policy from time to time. Changes will be
            posted on this page with an updated effective date. Continued use of
            the Site after changes are posted constitutes acceptance of the
            updated Policy where permitted by law.
          </Typography>

          <Typography variant="h6" gutterBottom>
            12. Contact Us
          </Typography>
          <Typography variant="body1" paragraph>
            Questions about this Privacy Policy:
            customerservice@strmonitoring.com.
          </Typography>
        </Box>
      </Container>
    </>
  );
};

export default TermsAndPrivacy;
